import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocketSubscription, useSocketMessage } from './useSocket';
import type { AxonEvent, WsServerMessage } from '@/lib/types';

interface UseStreamSocketOptions {
  /** The stream ID to subscribe to via the WebSocket. Also used to filter incoming events. */
  streamId: string | null;
  /** The React Query cache key array to write events into. */
  queryKey: readonly unknown[];
  /**
   * Replay only history above this sequence on subscribe (paged clients
   * subscribe at their loaded window's head). Omitted → full replay.
   */
  afterSequence?: number;
}

/**
 * Generic hook that subscribes to a WebSocket stream and writes incoming events
 * into a React Query cache entry identified by `queryKey`.
 *
 * ## Why events are buffered instead of written per-message
 *
 * A busy agent stream is a firehose: an active turn can push well over a
 * thousand `{ type: 'event' }` messages per second (token deltas, tool output
 * chunks, lifecycle notes). The naive shape — one `setQueryData` per message,
 * each rebuilding and re-deduplicating the whole array — makes every message
 * cost O(n) in the number of events already buffered, so a single turn is
 * O(n²), and it forces a fresh React render + full transcript style-recalc for
 * every token. Against a long transcript this pins the main thread (measured:
 * ~1000 msgs/sec driving 260ms+ style recalcs over a 70k-element DOM).
 *
 * Two changes cut that cost:
 *
 * 1. **Coalesce writes to one per animation frame.** Incoming events land in
 *    `bufferRef` and are flushed together on the next `requestAnimationFrame`,
 *    so ~1000 cache writes/sec collapse into ~60 — one render per painted frame.
 *    A hidden tab paints no frames, so there the flush is scheduled on a timer
 *    instead (see `scheduleFlush`).
 * 2. **Append, don't re-deduplicate.** A `Set` of ids already in the cache lets
 *    the flush skip duplicates and append only new events, so the per-flush cost
 *    is O(new events) instead of O(total). The id set is reused across flushes
 *    while this hook owns the array, and rebuilt only when the cache is replaced
 *    out from under us (initial REST load, reconnect backfill, cache reset).
 *
 * Callers that need a reconnect / back-fill strategy (e.g. agents) should add
 * their own `useEffect` on top of this hook; those infrequent full-array merges
 * still go through `deduplicateEvents` and simply reset the cached id set here.
 */
export function useStreamSocket({
  streamId,
  queryKey,
  afterSequence,
}: UseStreamSocketOptions): void {
  const queryClient = useQueryClient();

  useSocketSubscription(streamId, afterSequence);

  const queryKeyStr = JSON.stringify(queryKey);
  const parsedQueryKey = useMemo(
    () => JSON.parse(queryKeyStr) as readonly unknown[],
    [queryKeyStr],
  );

  // Events waiting to be written on the next frame.
  const bufferRef = useRef<AxonEvent[]>([]);
  const rafRef = useRef<number | null>(null);
  // The array this hook last wrote, and the id set that matches it. When the
  // cache still holds `cacheArrRef` we can extend the same id set incrementally;
  // any other identity means something external replaced the cache and we
  // rebuild the set once from the current contents.
  const cacheArrRef = useRef<AxonEvent[] | null>(null);
  const cacheIdsRef = useRef<Set<string> | null>(null);

  const flush = useCallback(() => {
    rafRef.current = null;
    const buffered = bufferRef.current;
    if (buffered.length === 0) return;
    bufferRef.current = [];

    queryClient.setQueryData<AxonEvent[]>(parsedQueryKey, (old) => {
      const base = old ?? [];
      let ids = cacheIdsRef.current;
      if (ids === null || base !== cacheArrRef.current) {
        ids = new Set(base.map((e) => e.id));
      }

      const toAppend: AxonEvent[] = [];
      for (const event of buffered) {
        if (ids.has(event.id)) continue;
        ids.add(event.id);
        toAppend.push(event);
      }

      if (toAppend.length === 0) {
        // Every buffered event was a duplicate; keep the existing array
        // identity so we don't trigger a no-op re-render.
        cacheArrRef.current = base;
        cacheIdsRef.current = ids;
        return base;
      }

      const next = [...base, ...toAppend];
      cacheArrRef.current = next;
      cacheIdsRef.current = ids;
      return next;
    });
  }, [queryClient, parsedQueryKey]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleFlush = useCallback(() => {
    if (typeof requestAnimationFrame === 'undefined') {
      flush();
      return;
    }
    // A hidden tab never paints, so `requestAnimationFrame` never fires there.
    // Scheduling on it would park every event in `bufferRef` for as long as the
    // tab stays in the background and leave the cache — and so the transcript —
    // frozen at whatever was last flushed. Timers are throttled rather than
    // stopped, so they keep background events landing in the cache in arrival
    // order (~1 write/sec) and the view is already correct when the tab comes
    // back. Note a frame requested while still visible will not fire either, so
    // it has to be cancelled and re-scheduled on the timer rather than awaited.
    if (typeof document !== 'undefined' && document.hidden) {
      if (rafRef.current !== null) {
        if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (timerRef.current !== null) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        flush();
      }, 0);
      return;
    }
    if (rafRef.current !== null || timerRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => flush());
  }, [flush]);

  // Reset the dedupe cache whenever the target stream/key changes, and drain
  // any still-buffered events on unmount so a fast unmount (e.g. session
  // switch) never drops the tail of a turn. The cleanup closes over the
  // previous `flush`, so leftovers land on the previous key.
  useEffect(() => {
    cacheArrRef.current = null;
    cacheIdsRef.current = null;
    return () => {
      if (rafRef.current !== null) {
        if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      flush();
    };
  }, [flush]);

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type !== 'event' || !streamId) return;
        if (msg.event.streamId !== streamId) return;
        bufferRef.current.push(msg.event);
        scheduleFlush();
      },
      [streamId, scheduleFlush],
    ),
  );
}
